<template>
  <UCard
    class="common-card"
    :class="[
      {
        'with-bg': background,
        'with-padding': padding,
        'with-hover': hover,
        'is-featured': featured,
      },
      hoverHideClasses,
      ui?.root,
    ]"
    data-slot="root"
    :ui="{
      body: ['max-md:p-2!', ui?.body],
    }"
  >
    <div
      class="image-wrapper"
      :class="ui?.imageWrapper"
      data-slot="image-wrapper"
    >
      <NuxtLink :to="to">
        <img
          v-if="image || item.image_url"
          class="image"
          :class="ui.image"
          :src="image || (item.image_url as string)"
          data-slot="image"
        />
      </NuxtLink>

      <div class="card-image-header">
        <slot name="image-top-left">
          <div
            v-if="imageTopLeft"
            class="image-top-left"
            :class="ui?.imageTopLeft"
            data-slot="image-top-left"
          >
            <template
              v-if="
                imageTopLeft === 'lesson-types' &&
                'lesson_types' in item &&
                item.lesson_types
              "
            >
              <UBadge
                v-for="(lessonType, index) in item.lesson_types"
                :key="index"
                class="badge lesson-type-badge"
                :class="ui?.badge"
                data-slot="badge"
              >
                <span class="truncate">{{ lessonType.name }}</span>
              </UBadge>

              <UBadge
                v-if="item.lesson_types?.length > 1"
                class="badge shrink-0 xl:hidden"
                :class="ui?.badge"
              >
                + {{ item.lesson_types?.length - 1 }}
              </UBadge>
            </template>

            <template
              v-else-if="
                imageTopLeft === 'directions' &&
                'directions' in item &&
                item.directions?.length
              "
            >
              <UBadge
                v-for="(direction, index) in item.directions"
                :key="index"
                class="badge lesson-type-badge"
                :class="ui?.badge"
                data-slot="badge"
                :icon="DIRECTION_ICONS[direction.slug]"
              >
                <span class="truncate">{{ direction.name }}</span>
              </UBadge>

              <UBadge
                v-if="item.directions?.length > 1"
                class="badge shrink-0 xl:hidden"
                :class="ui?.badge"
              >
                + {{ item.directions?.length - 1 }}
              </UBadge>
            </template>
          </div>
        </slot>

        <slot name="image-top-right">
          <div
            v-if="imageTopRight"
            class="badges"
            :class="ui?.badges"
            data-slot="image-top-right"
          >
            <template
              v-if="
                imageTopRight === 'rating' && 'rating' in item && item.rating
              "
            >
              <ULink :to="ratingLink">
                <UBadge
                  class="badge"
                  :class="ui?.ratingBadge"
                  leading-icon="i-local-star"
                  data-slot="rating-badge"
                >
                  {{ item.rating }}
                </UBadge>
              </ULink>
            </template>
          </div>
        </slot>
      </div>
    </div>

    <div class="body">
      <div
        class="body-header"
        :class="[
          {
            'with-school': titleAboveLeft === 'school',
            'hover-hide': (hoverElements || []).includes('body-header'),
          },
          ui?.bodyHeader,
        ]"
        data-slot="body-header"
      >
        <slot name="title-above-left">
          <DirectionIcons
            v-if="
              titleAboveLeft === 'directions' &&
              'directions' in item &&
              item?.directions?.length
            "
            :directions="item.directions"
          />

          <!-- TODO: заменить на реальные данные школы тренера (slide.school?.id) -->
          <NuxtLink
            v-else-if="titleAboveLeft === 'school'"
            :to="'source' in item ? `/schools/${item.source.id}` : '#'"
            class="flex items-center gap-1.5 text-accent-default typo-paragraph-xs-medium"
          >
            <Icon name="i-local-graduation-cap" />
            <div class="relative top-px">
              {{ ('source' in item && item.source.name) || '' }}
            </div>
          </NuxtLink>
        </slot>

        <slot name="title-above-right">
          <div v-if="titleAboveRight" class="flex items-center gap-2">
            <template v-for="(icon, index) in titleAboveRight" :key="index">
              <ULink
                v-if="typeof icon !== 'string'"
                class="flex justify-between gap-1.5"
                :to="icon.to"
              >
                <IconBadge
                  class="bg-accent-default text-white"
                  :class="icon.iconClass"
                  :icon="icon.icon"
                />
                <span :class="twMerge('text-accent-default', icon.labelClass)">
                  <span class="max-xl:hidden">Тренеры: </span>
                  {{ icon.value }}
                </span>
              </ULink>
            </template>
          </div>
        </slot>
      </div>

      <div
        class="title-wrapper flex justify-between items-center"
        :class="ui?.titleWrapper"
      >
        <h2 class="title flex items-start" :class="ui?.title">
          <NuxtLink :to="to">{{
            title ||
            ('name' in item && item.name) ||
            ('title' in item && item.title) ||
            ''
          }}</NuxtLink>
        </h2>

        <UBadge
          v-if="chip"
          class="lg:relative bottom-0.5 lg:text-[11px] py-0.5 ml-2.5 typo-paragraph-xs-medium"
        >
          {{ chip }}
        </UBadge>

        <slot name="title-right">
          <DirectionIcons
            v-if="
              titleRight === 'directions' &&
              'directions' in item &&
              item?.directions?.length
            "
            class="md:hidden!"
            :directions="item.directions"
          />
        </slot>
      </div>

      <p
        v-if="description || ('description' in item && item.description)"
        class="description line-clamp-2"
        :class="ui?.description"
      >
        {{ description || ('description' in item && item.description) }}
      </p>

      <div
        v-if="descriptionUnderLeft || descriptionUnderRight"
        class="description-under flex items-center justify-between"
      >
        <slot name="description-under-left">
          <div
            v-if="descriptionUnderLeft === 'date'"
            class="description-under-left"
          >
            <UBadge
              class="pl-0"
              :class="ui?.dateBadge"
              icon="i-local-calendar"
              color="neutral"
              variant="subtle"
              :ui="{
                base: 'ring-transparent',
              }"
            >
              {{ 'published_at' in item && getDate(item.published_at) }}
            </UBadge>
          </div>

          <div
            v-else-if="Array.isArray(descriptionUnderLeft)"
            class="description-under-left"
          >
            <template v-for="(btn, index) in descriptionUnderLeft" :key="index">
              <div v-if="btn.value" class="flex items-center">
                <IconBadge
                  class="bg-accent-default text-white mr-1.5"
                  :class="btn.iconClass"
                  :icon="btn.icon"
                  :ui="{
                    icon: 'w-4 h-4',
                  }"
                />
                <span class="typo-paragraph-xs-medium">
                  <span class="max-xl:hidden">{{ btn.label }}</span>
                  <span v-html="btn.value"></span>
                </span>
              </div>
            </template>
          </div>

          <div
            v-else-if="descriptionUnderLeft === 'trainer'"
            class="flex items-center text-accent-default text-sm"
          >
            <IconBadge
              class="bg-accent-default text-white mr-1.5"
              icon="i-local-coach"
            />
            <ULink class="text-accent-default">Моков Сергей Петрович</ULink>
          </div>
        </slot>

        <slot name="description-under-right">
          <DirectionIcons
            v-if="
              descriptionUnderRight === 'directions' &&
              ('direction' in item || 'directions' in item)
            "
            :directions="
              'directions' in item
                ? item.directions
                : 'direction' in item
                  ? [item.direction]
                  : []
            "
            :ui="{
              icon: 'w-6! h-6!',
            }"
          />
        </slot>
      </div>

      <div
        v-if="footer"
        class="footer"
        :class="[
          {
            'hover-hide': (hoverElements || []).includes('footer'),
          },
          ui.footer,
        ]"
      >
        <UButton
          v-for="(btn, index) in footer === true ? actions : footer"
          :key="index"
          class="w-full border border-accent-default ring-0"
          :variant="btn.variant || 'solid'"
          :to="btn.to"
          @click="btn.event ? $emit(btn.event?.name as any, item.id) : () => {}"
        >
          {{ btn.text }}
        </UButton>
      </div>
    </div>
  </UCard>
</template>

<script
  lang="ts"
  setup
  generic="
    T extends
      | School
      | Trainer
      | News
      | PrideEntry
      | (Direction & { rating: number })
  "
>
import type { Direction, News, PrideEntry, School, Trainer } from '~/api'
import type { ActionButton, IconWithLabel } from '~/types'
import DirectionIcons from './DirectionIcons.vue'
import { twMerge } from 'tailwind-merge'
import IconBadge from './IconBadge.vue'
import { dayjs, DIRECTION_ICONS } from '~/shared'

defineOptions({
  name: 'CommonCard',
})

defineEmits<{
  detail: [id: string]
  register: [id: string]
}>()

const props = withDefaults(
  defineProps<{
    item: T
    type?: 'school' | 'trainer' | 'news'
    to?: string
    ratingLink?: string
    padding?: boolean
    background?: boolean
    title?: string
    description?: string
    chip?: string | number
    image?: string
    hover?: boolean | string[]
    hoverElements?: ('body-header' | 'footer' | 'description')[]
    twClass?: string
    twImageClass?: string
    imageTopLeft?: 'lesson-types' | 'direction' | 'directions'
    imageTopRight?: 'rating'
    titleAboveLeft?: 'school' | 'directions'
    titleAboveRight?: 'school' | 'trainers' | IconWithLabel[]
    titleAboveRightClass?: string
    titleRight?: 'directions'
    descriptionUnderLeft?: 'date' | 'trainer' | IconWithLabel[]
    descriptionUnderRight?: 'directions'
    footer?: boolean | ActionButton[]
    featured?: boolean

    ui?: {
      root?: string
      body?: string
      image?: string
      imageWrapper?: string
      imageTopLeft?: string
      imageTopRight?: string
      badges?: string
      badge?: string
      dateBadge?: string
      bodyHeader?: string
      titleWrapper?: string
      title?: string
      titleAboveLeft?: string
      titleAboveRight?: string
      description?: string
      descriptionUnderLeft?: string
      descriptionUnderRight?: string
      ratingBadge?: string
      footer?: string
    }
  }>(),
  {
    image: '', // TODO: Добавить дефолтную заглушку
    padding: true,
    hover: false,
    background: true,
    hoverElements: () => ['body-header', 'footer'],
    featured: false,
    ui: () => ({}),
  },
)

const actions: ActionButton[] = [
  {
    event: {
      name: 'detail',
    },
    text: 'Подробнее',
    variant: 'outline',
    to: props.to,
  },
  {
    event: {
      name: 'register',
    },
    text: 'Записаться',
    variant: 'solid',
  },
]

const hoverHideClasses = computed(() => {
  if (Array.isArray(props.hover)) {
    return props.hover.map((block) => `hide-${block}`).join(' ')
  }

  return ''
})

const getDate = (date: Date | string): string =>
  dayjs(date).format('DD.MM.YYYY')
</script>

<style lang="scss" scoped>
.common-card {
  --tw-ring-color: transparent;
  --ui-bg: transparent;

  height: var(--card-height);
  color: var(--color-neutral-default);

  @include max-md {
    // height: calc(var(--card-height) * 1.433);
  }

  @include max-sm {
    min-height: fit-content;
  }

  &.with-padding {
    border-radius: 20px;

    @include max-sm {
      border-radius: 16px;
    }

    :deep([data-slot='body']) {
      padding: 10px;

      @include max-sm {
        padding: 4px;
      }
    }

    .body {
      padding: 20px;

      @include max-xl {
        padding: 16px 12px;
      }

      @include max-sm {
        padding: 8px;
      }
    }
  }

  &:not(.with-padding) {
    @include max-sm {
      height: calc(var(--card-height) + 2 * 4px);

      :deep([data-slot='body']) {
        padding: 4px;
      }
    }
  }

  &.with-bg {
    --ui-bg: var(--color-white);
  }

  &:not(.with-bg) {
    @include max-sm {
      background-color: var(--color-background-bg1);
    }
  }

  &.with-hover {
    @include isMoreThan(xl) {
      &:not(:hover) {
        .hover-hide {
          visibility: hidden;
          overflow: hidden;
          opacity: 0;
          transition:
            max-height 0.4s ease,
            opacity 0.4s ease,
            visibility 0.4s ease;
          max-height: 0;
          margin-top: 0;
          margin-bottom: 0;
        }
      }

      &:hover {
        .hover-hide {
          opacity: 1;
          visibility: visible;
          max-height: 500px;
        }

        .image {
          height: var(--card-hover-image-height, 250px);
        }
      }
    }
  }

  &.is-featured {
    height: calc(var(--card-height) + 20px);
    padding: 10px;
    background-color: var(--color-background-bg1);
  }

  :deep([data-slot='body']) {
    padding: 0;
    height: var(--card-height);
    min-height: fit-content;
  }

  .image-wrapper {
    position: relative;
    border-radius: var(--radius-16, 16px);

    @include max-md {
      margin-bottom: 10px;
    }
  }

  .image {
    border-radius: var(--radius-16, 16px);
    transition:
      height 0.4s ease,
      transform 0.4s ease-in-out;
  }

  .card-image-header {
    display: flex;
    justify-content: space-between;
    position: absolute;
    top: 10px;
    left: 10px;
    width: calc(100% - 20px);

    @include max-sm {
      gap: 10px;
    }
  }

  .image-top-left {
    display: flex;
    gap: 4px;
  }

  .badge {
    @include typo('paragraph-xs-medium');
    border: 1px solid var(--color-neutral-border-default);
    box-shadow: none;

    &.lesson-type-badge {
      @include max-xl {
        max-width: 140px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:not(:first-child) {
          display: none;
        }
      }

      @include max-md {
        max-width: 150px;
      }
    }
  }

  .body {
    padding: 24px 0;
    border-radius: var(--radius-20, 20px);

    @include max-xl {
      padding: 16px 0;
    }

    @include max-sm {
      padding: 8px;
    }
  }

  .body-header {
    display: flex;
    justify-content: space-between;

    &:not(:empty) {
      margin-bottom: 24px;

      &.with-school {
        margin-bottom: 12px;

        @include max-sm {
          display: none;
        }
      }

      @include max-xl {
        margin-bottom: 16px;
      }

      @include max-sm {
        margin-bottom: 12px;
      }
    }
  }

  .title-wrapper {
    --ui-text-muted: var(--color-neutral-default);
    margin-bottom: 8px;

    @include max-xl {
      margin-bottom: 8px;
    }

    @include max-sm {
      margin-bottom: 0;
    }
  }

  .title {
    @include typo('heading-h4');

    @include max-md {
      max-width: calc(100% - 30px);
      // @include typo('heading-h3');
      font-size: 18px;
      line-height: 1.5;
    }
  }

  .description {
    @include typo('paragraph-sm-regular');
    font-family: var(--font-lato);

    @include max-md {
      // overflow: hidden;
      display: -webkit-box;
      margin-top: 8px;
    }
  }

  .description-under {
    margin-top: 20px;

    @include max-xl {
      margin-top: 16px;
    }

    @include max-md {
      margin-top: 12px;
    }
  }

  .description-under-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .footer {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 24px;

    &.hover-hide {
      margin-top: 24px;
    }

    @include max-xl {
      margin-top: 16px;
    }

    @include max-sm {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.common-card {
  --card-height: 514px;
  --card-image-height: 374px;
  --card-hover-image-height: 250px;

  @include max-xl {
    --card-height: 394px;
    --card-image-height: 180px;
  }

  @include max-md {
    --card-height: 340px;
    --card-image-height: 160px;
  }

  .image {
    width: 100%;
    height: var(--card-image-height, 374px);
    object-fit: cover;
    object-position: top;

    @include max-md {
      height: calc(var(--card-image-height, 250px) * 1.2);
      line-clamp: 2;
    }
  }

  .description {
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .badge {
    height: 24px;
    @include typo(paragraph-xs-medium);
  }
}
</style>
